import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  HELP_ARTICLES,
  HELP_CHAPTERS,
  HELP_GROUPS,
  articleDisplayTitle,
  findArticle,
  searchHelp,
  searchTerms,
  type HelpArticle,
  type HelpChapter,
  type HelpGroup,
} from '../help/guide';
import { HelpMarkdown } from '../help/HelpMarkdown';

/** Hand-picked answers to the questions members and admins ask most. */
const POPULAR: { slug: string; label: string }[] = [
  { slug: '2-members', label: 'Quick start for members' },
  { slug: '4-3-new-device-verification-code', label: 'I need a sign-in code' },
  { slug: '5-6-tasting-page-and-rsvp', label: 'RSVP and the waitlist' },
  { slug: '5-7-live-tasting-scoring', label: 'Scoring a live tasting' },
  { slug: '6-7-live-scoring', label: 'Scoring on the app with bad signal' },
  { slug: '2-administrators', label: 'Quick start for administrators' },
  { slug: '9-running-a-tasting-night-admin-runbook', label: 'Running a tasting night' },
  { slug: '14-faq-and-troubleshooting', label: 'FAQ and troubleshooting' },
];

const chapterLabel = (c: HelpChapter) => `${c.number}. ${c.title}`;

function SearchIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

function HelpSearchBox({
  value,
  onChange,
  onSubmit,
  autoFocus,
  size = 'lg',
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: () => void;
  autoFocus?: boolean;
  size?: 'lg' | 'sm';
}) {
  const ref = useRef<HTMLInputElement>(null);

  // "/" jumps to the help search, as on most documentation sites.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit?.();
  }

  return (
    <form role="search" onSubmit={submit} className="relative">
      <label htmlFor={`help-search-${size}`} className="sr-only">
        Search help articles
      </label>
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-cream-300/60">
        <SearchIcon className={size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />
      </span>
      <input
        id={`help-search-${size}`}
        ref={ref}
        type="search"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          size === 'lg'
            ? 'Search help — e.g. "waitlist", "quarter points", "invoice"'
            : 'Search help…'
        }
        className={`field ${size === 'lg' ? 'py-3 pl-11 pr-12 text-base' : 'pl-9 pr-3 text-sm'}`}
      />
      {size === 'lg' && (
        <kbd className="pointer-events-none absolute inset-y-0 right-3 my-auto hidden h-6 items-center rounded border border-ink-800 px-1.5 text-xs text-cream-300/60 sm:flex">
          /
        </kbd>
      )}
    </form>
  );
}

function SearchResults({ query }: { query: string }) {
  const results = useMemo(() => searchHelp(query), [query]);
  const terms = searchTerms(query);

  if (terms.length === 0) {
    return <p className="mt-6 text-sm text-cream-300/70">Type at least two letters to search.</p>;
  }
  if (results.length === 0) {
    return (
      <div className="card mt-6 text-center">
        <p className="text-cream-100">No articles match “{query}”.</p>
        <p className="mt-2 text-sm text-cream-300/70">
          Try a simpler word, or browse the{' '}
          <Link to="/help/14-faq-and-troubleshooting" className="text-gold-200 hover:underline">
            FAQ and troubleshooting
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <p className="text-sm text-cream-300/70" aria-live="polite">
        {results.length} {results.length === 1 ? 'article' : 'articles'} for “{query}”
      </p>
      <ul className="mt-3 space-y-3">
        {results.map(({ article, snippet }) => (
          <li key={article.slug}>
            <Link
              to={`/help/${article.slug}?q=${encodeURIComponent(query)}`}
              className="card block p-4 transition hover:border-gold-600/40"
            >
              <p className="text-xs uppercase tracking-wide text-gold-400">
                {article.chapter.group} · {chapterLabel(article.chapter)}
              </p>
              <h3 className="mt-1 text-lg text-cream-50">{articleDisplayTitle(article)}</h3>
              <p className="mt-1 text-sm text-cream-300/80">
                <Highlighted text={snippet} terms={terms} />
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Highlighted({ text, terms }: { text: string; terms: string[] }) {
  if (terms.length === 0) return <>{text}</>;
  const re = new RegExp(
    `(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'giu',
  );
  return (
    <>
      {text.split(re).map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="rounded-sm bg-gold-400/30 text-cream-50">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// /help — the index
// ---------------------------------------------------------------------------

export function HelpIndex() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [group, setGroup] = useState<HelpGroup | 'All'>('All');

  const setQuery = (q: string) => {
    const next = new URLSearchParams(params);
    if (q) next.set('q', q);
    else next.delete('q');
    setParams(next, { replace: true });
  };

  const chapters = group === 'All' ? HELP_CHAPTERS : HELP_CHAPTERS.filter((c) => c.group === group);

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-gold-400">Help centre</p>
      <h1 className="mt-2 font-display text-3xl text-cream-50 sm:text-4xl">How can we help?</h1>
      <p className="mt-2 max-w-2xl text-sm text-cream-300/80">
        The complete guide to the club’s website and mobile app — for members and administrators.
        Every screen and feature, what it’s for, and how to use it. {HELP_ARTICLES.length} articles.
      </p>

      <div className="mt-6 max-w-2xl">
        <HelpSearchBox value={query} onChange={setQuery} autoFocus />
      </div>

      {query ? (
        <SearchResults query={query} />
      ) : (
        <>
          <section className="mt-10" aria-labelledby="help-popular">
            <h2 id="help-popular" className="text-xl text-gold-200">
              Popular articles
            </h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {POPULAR.filter((p) => findArticle(p.slug)).map((p) => (
                <Link
                  key={p.slug}
                  to={`/help/${p.slug}`}
                  className="card p-4 text-sm text-cream-100 transition hover:border-gold-600/40 hover:text-gold-200"
                >
                  {p.label} →
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-12" aria-labelledby="help-index">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 id="help-index" className="text-xl text-gold-200">
                Index
              </h2>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by audience">
                {(['All', ...HELP_GROUPS] as const).map((g) => (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={group === g}
                    onClick={() => setGroup(g)}
                    className={`rounded-full border px-3 py-1 text-xs transition ${
                      group === g
                        ? 'border-gold-500 bg-gold-500 text-on-gold'
                        : 'border-ink-800 text-cream-200 hover:border-gold-600/50'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 grid items-start gap-5 md:grid-cols-2">
              {chapters.map((c) => (
                <div key={c.slug} className="card p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gold-400">{c.group}</p>
                  <h3 className="mt-1 text-lg text-cream-50">
                    <Link
                      to={`/help/${c.articles[0]?.slug ?? c.slug}`}
                      className="hover:text-gold-200"
                    >
                      {chapterLabel(c)}
                    </Link>
                  </h3>
                  {c.articles.length > 1 && (
                    <ul className="mt-3 space-y-1 text-sm">
                      {c.articles.map((a) => (
                        <li key={a.slug}>
                          <Link
                            to={`/help/${a.slug}`}
                            className="text-cream-200 hover:text-gold-200 hover:underline"
                          >
                            {a.slug === c.slug ? 'Overview' : articleDisplayTitle(a)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>

          <p className="mt-10 text-sm text-cream-300/70">
            Prefer a document?{' '}
            <a href="/GWC_User_Guide.docx" download className="text-gold-200 hover:underline">
              Download the complete guide (Word)
            </a>
            .
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// /help/:slug — an article
// ---------------------------------------------------------------------------

function Sidebar({ current }: { current: HelpArticle }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Set<string>>(() => new Set([current.chapter.slug]));

  useEffect(() => {
    setOpen((prev) =>
      prev.has(current.chapter.slug) ? prev : new Set(prev).add(current.chapter.slug),
    );
  }, [current.chapter.slug]);

  const toggle = (slug: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  return (
    <nav aria-label="Help articles" className="space-y-4">
      <HelpSearchBox
        size="sm"
        value={query}
        onChange={setQuery}
        onSubmit={() => query.trim() && navigate(`/help?q=${encodeURIComponent(query.trim())}`)}
      />
      <Link to="/help" className="block text-sm text-gold-200 hover:underline">
        ← Help index
      </Link>
      <ul className="space-y-1 text-sm">
        {HELP_CHAPTERS.map((c) => {
          const expanded = open.has(c.slug);
          const single = c.articles.length === 1;
          return (
            <li key={c.slug}>
              {single ? (
                <Link
                  to={`/help/${c.articles[0]!.slug}`}
                  aria-current={c.articles[0]!.slug === current.slug ? 'page' : undefined}
                  className={`block rounded px-2 py-1 ${
                    c.articles[0]!.slug === current.slug
                      ? 'bg-wine-700 text-on-wine'
                      : 'text-cream-100 hover:text-gold-200'
                  }`}
                >
                  {chapterLabel(c)}
                </Link>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => toggle(c.slug)}
                    aria-expanded={expanded}
                    className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-cream-100 hover:text-gold-200"
                  >
                    <span>{chapterLabel(c)}</span>
                    <span aria-hidden="true" className="text-xs text-cream-300/60">
                      {expanded ? '−' : '+'}
                    </span>
                  </button>
                  {expanded && (
                    <ul className="mb-2 ml-2 mt-1 space-y-0.5 border-l border-ink-800 pl-2">
                      {c.articles.map((a) => (
                        <li key={a.slug}>
                          <Link
                            to={`/help/${a.slug}`}
                            aria-current={a.slug === current.slug ? 'page' : undefined}
                            className={`block rounded px-2 py-0.5 text-[13px] ${
                              a.slug === current.slug
                                ? 'bg-wine-700 text-on-wine'
                                : 'text-cream-300 hover:text-gold-200'
                            }`}
                          >
                            {a.slug === c.slug ? 'Overview' : articleDisplayTitle(a)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function HelpArticlePage() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const article = findArticle(slug);
  const highlightTerms = searchTerms(params.get('q') ?? '');

  useEffect(() => {
    if (hash) {
      // Wait a frame so the article has rendered before looking for the anchor.
      requestAnimationFrame(() =>
        document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView(),
      );
    } else {
      window.scrollTo(0, 0);
    }
  }, [slug, hash]);

  useEffect(() => {
    if (!article) return;
    const previous = document.title;
    document.title = `${article.title} · Help · Gentlemen’s Wine Club`;
    return () => {
      document.title = previous;
    };
  }, [article]);

  if (!article) {
    return (
      <div className="card mx-auto max-w-lg text-center">
        <h1 className="font-display text-2xl text-cream-50">Article not found</h1>
        <p className="mt-2 text-sm text-cream-300/80">That help article may have moved.</p>
        <Link to="/help" className="btn-primary mt-4">
          Search help
        </Link>
      </div>
    );
  }

  const index = HELP_ARTICLES.indexOf(article);
  const prev = HELP_ARTICLES[index - 1];
  const next = HELP_ARTICLES[index + 1];
  const isOverview = article.slug === article.chapter.slug;

  return (
    <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="order-2 lg:order-1">
        <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-2">
          <Sidebar current={article} />
        </div>
      </aside>

      <article className="order-1 min-w-0 lg:order-2">
        <nav aria-label="Breadcrumb" className="text-xs text-cream-300/70">
          <Link to="/help" className="hover:text-gold-200">
            Help
          </Link>
          <span className="mx-1.5">/</span>
          <span>{article.chapter.group}</span>
          <span className="mx-1.5">/</span>
          <Link
            to={`/help/${article.chapter.articles[0]?.slug ?? article.chapter.slug}`}
            className="hover:text-gold-200"
          >
            {chapterLabel(article.chapter)}
          </Link>
        </nav>

        <h1 className="mt-3 font-display text-3xl text-cream-50 sm:text-4xl">
          {isOverview ? chapterLabel(article.chapter) : articleDisplayTitle(article)}
        </h1>

        {article.headings.length > 1 && (
          <div className="card mt-6 p-4">
            <p className="text-xs uppercase tracking-wide text-gold-400">On this page</p>
            <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
              {article.headings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className="text-cream-200 hover:text-gold-200 hover:underline"
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6">
          <HelpMarkdown source={article.body} highlightTerms={highlightTerms} />
        </div>

        {isOverview && article.chapter.articles.length > 1 && (
          <div className="mt-8">
            <h2 className="text-lg text-gold-200">In this chapter</h2>
            <ul className="mt-2 grid gap-2 sm:grid-cols-2">
              {article.chapter.articles
                .filter((a) => a.slug !== article.slug)
                .map((a) => (
                  <li key={a.slug}>
                    <Link
                      to={`/help/${a.slug}`}
                      className="card block p-3 text-sm text-cream-100 hover:border-gold-600/40 hover:text-gold-200"
                    >
                      {articleDisplayTitle(a)}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        )}

        <div className="mt-12 grid gap-3 border-t border-ink-800 pt-6 sm:grid-cols-2">
          {prev ? (
            <Link
              to={`/help/${prev.slug}`}
              className="card block p-4 transition hover:border-gold-600/40"
            >
              <p className="text-xs text-cream-300/70">← Previous</p>
              <p className="mt-1 text-sm text-cream-50">{articleDisplayTitle(prev)}</p>
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link
              to={`/help/${next.slug}`}
              className="card block p-4 text-right transition hover:border-gold-600/40"
            >
              <p className="text-xs text-cream-300/70">Next →</p>
              <p className="mt-1 text-sm text-cream-50">{articleDisplayTitle(next)}</p>
            </Link>
          )}
        </div>
      </article>
    </div>
  );
}
